import * as React from "react";
import { RouteComponentProps } from "react-router";
import { connect } from "react-redux";
import Loading from "../../../components/Loading";
import { RootState } from "../../types";
import { SurveyStep, Question } from "../types";
import AnswerQuestion from "../components/Question";
import { goToSurveyStep } from "../actions";
import PatientHeader from "../../../components/PatientHeader";
import Landing from "../components/Landing";
import "./SurveyContainer.scss";
import PatientInfo from "../components/PatientInfo";
import SentConfirmation from "../components/SentConfirmation";
import Resolve from "../components/Resolve";

interface StoreProps {
  step: SurveyStep;
  loading: boolean;
}

interface DispatchProps {
  goToSurveyStep: (step: SurveyStep) => void;
}

interface OwnState {
  isBusy: boolean;
  covidId: string;
  questions: Question[];
  resolveQuestion: Question | null;
  adverseAnswers: Question[];
  currentQuestionId: number;
}

type Props = OwnState & DispatchProps & StoreProps & RouteComponentProps<{ covidId: string }>;

class SurveyContainer extends React.PureComponent<Props, OwnState> {
  public constructor(props: Props) {
    super(props);

    this.state = {
      isBusy: false,
      covidId: this.props.match.params.covidId,
      questions: this.getCovidQuestions(),
      resolveQuestion: null,
      adverseAnswers: [],
      currentQuestionId: 0
    };

    this.sendMessageClicked = this.sendMessageClicked.bind(this);
  }

  private getCovidQuestions = () => {
    return [
      {
        Id: 1,
        Question: "Are you experiencing the following symptoms?",
        LineItems: [
          "Severe trouble breathing",
          "Persistent pain or pressure in chest",
          "New confusion or inability to arouse",
          "Bluish lips or face"
        ],
        SelectedAnswer: null,
        Answers: [
          {
            Answer: "Yes",
            IsAberrant: true,
            Resolve: "Call 911 or go to the your nearest emergency Department."
          },
          { Answer: "No", IsAberrant: false }
        ]
      },
      {
        Id: 2,
        Question:
          "Are you over the age of 65, have a chronic medical condition (e.g. heart disease, diabetes), or are you immunocompromised?",
        SelectedAnswer: null,
        Answers: [
          { Answer: "Yes", IsAberrant: true },
          { Answer: "No", IsAberrant: false }
        ]
      },
      {
        Id: 3,
        Question: "Are you experiencing fever, cough, or shortness of breath?",
        SelectedAnswer: null,
        Answers: [
          { Answer: "Yes", IsAberrant: true },
          { Answer: "No", IsAberrant: false }
        ]
      },
      {
        Id: 4,
        Question:
          "Are you returning from a place with widespread, ongoing community spread of COVID-19?",
        SelectedAnswer: null,
        Answers: [
          { Answer: "Yes", IsAberrant: true },
          { Answer: "No", IsAberrant: false }
        ]
      },
      {
        Id: 5,
        Question: "Have you had close contact with a suspected or confirmed COVID-19 patient?",
        SelectedAnswer: null,
        Answers: [
          { Answer: "Yes", IsAberrant: true },
          { Answer: "No", IsAberrant: false }
        ]
      }
    ];
  };

  private sendMessageClicked() {
    this.setState({ isBusy: true });
  }

  private processQuestion = (question: Question) => {
    const questions: Question[] = this.state.questions.map(a => ({ ...a }));
    questions[this.state.currentQuestionId].SelectedAnswer = question.SelectedAnswer;

    if (question.SelectedAnswer && question.SelectedAnswer.Resolve) {
      this.setState({ resolveQuestion: question });
      this.props.goToSurveyStep(SurveyStep.Resolve);
    } else {
      const nextId = this.state.currentQuestionId + 1;

      if (nextId < questions.length) {
        this.setState({
          questions: questions,
          currentQuestionId: nextId
        });
      } else {
        this.setState({
          questions: questions,
          currentQuestionId: nextId === questions.length ? this.state.currentQuestionId : nextId,
          adverseAnswers: questions.filter(q => q.SelectedAnswer && q.SelectedAnswer.IsAberrant)
        });
        this.props.goToSurveyStep(SurveyStep.PatientInfo);
      }
    }
  };

  private renderComponent(step: SurveyStep) {
    switch (step) {
      case SurveyStep.Landing:
        return <Landing goToCovidStep={this.props.goToSurveyStep} />;
      case SurveyStep.AnswerQuestion:
        return (
          <AnswerQuestion
            goToCovidStep={this.props.goToSurveyStep}
            question={this.state.questions[this.state.currentQuestionId]}
            processQuestion={this.processQuestion}
          />
        );
      case SurveyStep.Resolve:
        return <Resolve question={this.state.resolveQuestion} />;
      case SurveyStep.PatientInfo:
        return (
          <PatientInfo
            goToCovidStep={this.props.goToSurveyStep}
            processQuestion={this.processQuestion}
          />
        );
      case SurveyStep.SentConfirmation:
        return <SentConfirmation />;
    }
  }

  public render() {
    const { loading } = this.props;
    return (
      <>
        <div className="view-container covid">
          <PatientHeader />
          {this.renderComponent(this.props.step)}
        </div>
        {loading && <Loading css="patient" />}
      </>
    );
  }
}

export default connect<StoreProps, DispatchProps, {}, RootState>(
  state => ({
    step: state.survey.data.step,
    loading: state.survey.loading
  }),
  { goToSurveyStep }
)(SurveyContainer);
